
import { nanoid } from 'nanoid';
import { VideoDashboardStore, Folder, Video } from './types';
import { generateRandomColor } from './utils';

/**
 * Store actions for folder management
 */
export const createFolderActions = (set: any, get: any): Pick<VideoDashboardStore, 
  'addFolder' | 'renameFolder' | 'deleteFolder' | 'moveVideoToFolder' | 'setActiveFolder' | 'recalculateFolderCounts'
> => ({
  addFolder: ((name, options) => {
    const id = nanoid();
    
    const newFolder: Folder = {
      id,
      name,
      color: options?.color ?? generateRandomColor(),
      emoji: options?.emoji ?? '📁',
      count: 0,
      createdAt: new Date().toISOString()
    };
    
    set((state: VideoDashboardStore) => ({
      folders: [...state.folders, newFolder]
    }));
    
    return id;
  }) as VideoDashboardStore['addFolder'],
  
  renameFolder: (id, name) => {
    set((state: VideoDashboardStore) => ({
      folders: state.folders.map((folder) => 
        folder.id === id ? { ...folder, name } : folder
      )
    }));
  },
  
  deleteFolder: (id) => {
    set((state: VideoDashboardStore) => {
      // Move videos from this folder to no folder
      const updatedVideos = state.videos.map((video) => 
        video.folderId === id ? { ...video, folderId: null } : video
      );
      
      return {
        folders: state.folders.filter((folder) => folder.id !== id),
        videos: updatedVideos,
        activeFolder: state.activeFolder === id ? null : state.activeFolder
      };
    });
  },
  
  moveVideoToFolder: (videoId, folderId) => {
    set((state: VideoDashboardStore) => {
      // First update the video's folder assignment
      const updatedVideos = state.videos.map((video) => 
        video.id === videoId ? { ...video, folderId } : video
      );
      
      // Then recalculate folder counts
      const updatedFolders = state.folders.map((folder) => {
        const count = updatedVideos.filter(v => v.folderId === folder.id).length;
        return { ...folder, count };
      });
      
      return {
        videos: updatedVideos,
        folders: updatedFolders
      };
    });
  },
  
  setActiveFolder: (folderId) => {
    set({ activeFolder: folderId });
  },
  
  recalculateFolderCounts: () => {
    set((state: VideoDashboardStore) => {
      const updatedFolders = state.folders.map((folder) => {
        const count = state.videos.filter(v => v.folderId === folder.id).length;
        return { ...folder, count };
      });
      
      return { folders: updatedFolders };
    });
  }
});

/**
 * Store actions for view and filter settings
 */
export const createViewActions = (set: any): Pick<VideoDashboardStore, 
  'setViewMode' | 'setSearchQuery' | 'setStatusFilter'
> => ({
  setViewMode: (mode) => {
    set({ viewMode: mode });
  },
  
  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },
  
  setStatusFilter: (status) => {
    set({ statusFilter: status });
  }
});

/**
 * Store actions for video management
 */
export const createVideoActions = (set: any): Pick<VideoDashboardStore,
  'addVideo'
> => ({
  addVideo: (video) => {
    set((state: VideoDashboardStore) => ({
      videos: [...state.videos, video]
    }));
  }
});

